import { ChakraFlex, MaudSectionHeader, Text } from '@equip.health/ui';

import { InquiryEvents } from '~/analytics';
import { PAGE_TOP_PADDING } from '~/lib/constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import Loading from '~/pages/loading';
import {
  useGetAdminEpisodesByPatientIdQuery,
  useGetPatientTreatmentEpisodesQuery,
} from '~/store/services/clinicalData';
import { useGetPatientInquiriesQuery } from '~/store/services/inquiry';

import PatientAdminEpisodeList from './PatientAdminEpisodeList';
import PatientInquiryList from './PatientInquiryList';
import PatientTreatmentEpisodeList from './PatientTreatmentEpisodeList';

const TOP_LEVEL_ID = 'enrollment-tab';

const SectionWrapper = ({ children }: React.PropsWithChildren): JSX.Element => {
  return (
    <ChakraFlex flexDirection="column" gap="24px" paddingY={PAGE_TOP_PADDING}>
      {children}
    </ChakraFlex>
  );
};

const EnrollmentSection = ({
  children,
  isLoading,
}: { isLoading: boolean } & React.PropsWithChildren): JSX.Element => {
  return (
    <ChakraFlex flexDirection="column" gap="12px">
      {isLoading ? <Loading /> : children}
    </ChakraFlex>
  );
};

interface InquiryEnrollmentProps {
  patientId: string;
}

const InquiryEnrollment = ({ patientId }: InquiryEnrollmentProps) => {
  const {
    data: patientInquiries,
    isLoading: isPatientInquiriesLoading,
    isError: isPatientInquiriesError,
  } = useGetPatientInquiriesQuery(patientId);

  const {
    data: patientTreatmentEpisodes,
    isLoading: isPatientTreatmentEpisodesLoading,
  } = useGetPatientTreatmentEpisodesQuery(patientId, {
    skip: !patientId,
  });

  const {
    data: patientAdminEpisodes,
    isLoading: isPatientAdminEpisodesLoading,
  } = useGetAdminEpisodesByPatientIdQuery(patientId);

  const { setComponentVisibility } = useComponentPortalContext();
  const { trackEvent } = useAnalytics();

  const handleAddInquiry = () => {
    setComponentVisibility({
      component: PortalComponent.CREATE_INQUIRY_DRAWER,
      isVisible: true,
    });
    trackEvent(InquiryEvents.addInquiryClicked);
  };

  return (
    <ChakraFlex flexDirection="column" gap="24px">
      <SectionWrapper>
        <MaudSectionHeader
          count={patientTreatmentEpisodes?.data.length}
          id={`${TOP_LEVEL_ID}__treatment-episodes`}
          style="secondary"
          title="Treatment episodes"
        />
        <EnrollmentSection isLoading={isPatientTreatmentEpisodesLoading}>
          <PatientTreatmentEpisodeList
            episodes={patientTreatmentEpisodes}
            id={TOP_LEVEL_ID}
          />
        </EnrollmentSection>
      </SectionWrapper>
      <SectionWrapper>
        <MaudSectionHeader
          count={patientInquiries?.length}
          id={`${TOP_LEVEL_ID}__inquiries`}
          onAdd={handleAddInquiry}
          onAddTitle="Add inquiry"
          style="secondary"
          title="Inquiries"
        />
        <EnrollmentSection isLoading={isPatientInquiriesLoading}>
          {isPatientInquiriesError ? (
            <ChakraFlex
              alignItems="center"
              justifyContent="center"
              minHeight="100px"
              padding="16px"
            >
              <Text
                aria-live="polite"
                color="red.500"
                role="alert"
              >
                Failed to load inquiries. Please try again later.
              </Text>
            </ChakraFlex>
          ) : (
            <PatientInquiryList id={TOP_LEVEL_ID} inquiries={patientInquiries} />
          )}
        </EnrollmentSection>
      </SectionWrapper>
      <SectionWrapper>
        <MaudSectionHeader
          count={patientAdminEpisodes?.length}
          id={`${TOP_LEVEL_ID}__admin-episodes`}
          style="secondary"
          title="Admin episodes (archive)"
        />
        <EnrollmentSection isLoading={isPatientAdminEpisodesLoading}>
          <PatientAdminEpisodeList
            episodes={patientAdminEpisodes}
            id={TOP_LEVEL_ID}
          />
        </EnrollmentSection>
      </SectionWrapper>
    </ChakraFlex>
  );
};

export default InquiryEnrollment;
